<template>
  <Transition appear name="v-fade" @after-leave="destroyElement">
    <div
      v-show="!placement || visible"
      ref="el"
      :class="[
        'v-notification',
        typeClass,
        {
          'v-notification--popup': placement,
          'v-notification--clickable': onClick !== undefined,
          'has-message': message || $slots.default,
          'is-closable': closable,
          'transparent-on-hover': clickThrough,
        },
      ]"
      role="alert"
      :style
      @click="click"
      @mouseenter="clearTimer()"
      @mouseleave="startTimer()"
    >
      <h2 v-if="title" class="v-notification__title">
        <VIcon v-if="loading" icon="Refresh" rotate style="margin-right: 10px" :type="type === 'dark' ? 'white' : 'black'" />
        {{ title }}
      </h2>
      <div v-show="message || $slots.default" class="v-notification__content">
        <slot>
          <!-- eslint-disable-next-line -->
          <p v-if="dangerouslyUseHTMLString" v-html="message" />
          <p v-else v-text="message" />
        </slot>
      </div>

      <VIcon v-if="closable" class="v-notification__close" icon="Close" :size="21" @click.stop="close()" />

      <VProgress v-if="progress !== undefined" class="v-progress--notification" :height="4" track-color="rgba(0,0,0,0.05)" :type="progressType" :value="progress" />
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from 'vue'

import type { VNotificationPlacement, VNotificationType, VProgressType } from '../../types.ts'
import VIcon from '../Icon/VIcon.vue'
import VProgress from '../VProgress.vue'

const mapToProgressType: Record<string, VProgressType> = {
  warning: 'yellow',
  success: 'green',
  error: 'red',
  info: 'dark',
  dark: 'white',
  purple: 'dark',
}

interface Props {
  title?: string
  message?: string
  duration?: number
  type?: VNotificationType
  placement?: VNotificationPlacement
  onClick?: (notification: VNotificationExposed) => any
  onClose?: (notification: VNotificationExposed) => any
  progress?: number
  closable?: boolean
  dangerouslyUseHTMLString?: boolean
  loading?: boolean
  clickThrough?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  message: '',
  duration: 0,
  type: '',
  placement: '',
  onClick: undefined,
  onClose: undefined,
  progress: undefined,
  closable: false,
  dangerouslyUseHTMLString: false,
  loading: false,
  clickThrough: false,
})

defineSlots<{
  default?(): any
}>()

const visible = ref(true)
const closed = ref(false)
const verticalOffset = ref(0)
const timer = ref<number | undefined>(undefined)
const destroy = ref<(() => void) | null>(null)
const el = useTemplateRef('el')

const progressType = computed(() => mapToProgressType[props.type] ?? props.type)
const typeClass = computed(() => (props.type ? `v-notification--${props.type}` : ''))
const verticalProperty = computed(() => (props.placement.startsWith('top') ? 'top' : 'bottom'))
const style = computed(() => ({ [verticalProperty.value]: `${verticalOffset.value}px` }))

onMounted(() => {
  if (props.duration > 0) {
    startTimer()
  }
})

function destroyElement() {
  if (destroy.value) {
    el.value?.parentNode?.parentNode?.removeChild(el.value?.parentNode)
    destroy.value()
  }
}

function click({ clientX, clientY }: MouseEvent) {
  if (props.clickThrough && el.value) {
    const style = el.value.style.display
    el.value.style.display = 'none'
    const element = document.elementFromPoint(clientX, clientY) as HTMLElement
    element?.click()
    el.value.style.display = style

    return
  }

  if (typeof props.onClick === 'function') {
    props.onClick(exposed)
  }
}

function close() {
  closed.value = true
  visible.value = false

  if (typeof props.onClose === 'function') {
    props.onClose(exposed)
  }
}

function clearTimer() {
  clearTimeout(timer.value)
}

function startTimer() {
  if (props.duration > 0) {
    timer.value = window.setTimeout(() => {
      if (!closed.value) {
        close()
      }
    }, props.duration)
  }
}

const exposed = {
  click,
  close,
  closed,
  destroy,
  destroyElement,
  duration: props.duration,
  visible,
}

export type VNotificationExposed = typeof exposed

defineExpose(exposed)
</script>

<style>
#v-notifications-top,
#v-notifications-top-left,
#v-notifications-top-right,
#v-notifications-bottom-left,
#v-notifications-bottom-right {
  pointer-events: none;
}

#v-notifications-top {
  left: 50%;
  transform: translateX(-50%);
}

#v-notifications-top-right,
#v-notifications-bottom-right {
  position: fixed;
  right: 0px;
}

#v-notifications-top-left,
#v-notifications-bottom-left {
  position: fixed;
  left: 0px;
}

#v-notifications-top-left,
#v-notifications-top-right,
#v-notifications-top {
  position: fixed;
  top: 0px;
}

#v-notifications-bottom-left,
#v-notifications-bottom-right {
  position: fixed;
  bottom: 0px;
}

.v-notification {
  position: relative;
  padding: 14px;
  box-sizing: border-box;
  background-color: #fff;
  border: var(--border);
  border-radius: var(--border-radius);
  pointer-events: all;
  overflow: hidden;

  transition:
    box-shadow 0.3s,
    opacity 0.3s,
    transform 0.3s,
    left 0.3s,
    right 0.3s,
    top 0.4s,
    bottom 0.3s;
}

.v-notification.transparent-on-hover:hover {
  opacity: 0.3;
}

.v-notification--popup {
  width: 330px;
  box-shadow: var(--box-shadow-light);
  margin: 16px;
  border-color: #fff;
}

.v-notification--clickable {
  cursor: pointer;

  &:hover {
    box-shadow: var(--box-shadow);
  }
}

.v-notification--primary {
  color: var(--color-primary);
  background-color: var(--color-blue-2);
  border-color: var(--color-blue-4);
}

.v-notification--primary .v-notification__close * {
  stroke: var(--color-primary);
}

.v-notification--info {
  background-color: var(--color-grey-6);
  border-color: var(--color-grey-20);
}

.v-notification--success {
  color: var(--color-green-dark);
  background-color: var(--color-green-lightest);
  border-color: var(--color-green);
}

.v-notification--success .v-notification__close * {
  stroke: var(--color-green-dark);
}

.v-notification--warning {
  color: var(--color-yellow-dark);
  background-color: var(--color-yellow-lightest);
  border-color: var(--color-yellow);
}

.v-notification--warning .v-notification__close * {
  stroke: var(--color-yellow-dark);
}

.v-notification--error {
  color: var(--color-red);
  background-color: var(--color-red-lightest);
  border-color: var(--color-red);
}

.v-notification--error .v-notification__close * {
  stroke: var(--color-red);
}

.v-notification--green {
  color: var(--color-green-dark);
  background-color: var(--color-green-lightest);
  border-color: var(--color-green);
}

.v-notification--green .v-notification__close * {
  stroke: var(--color-green-dark);
}

.v-notification--yellow {
  color: var(--color-yellow-dark);
  background-color: var(--color-yellow-lightest);
  border-color: var(--color-yellow);
}

.v-notification--yellow .v-notification__close * {
  stroke: var(--color-yellow-dark);
}

.v-notification--red {
  color: var(--color-red);
  background-color: var(--color-red-lightest);
  border-color: var(--color-red);
}

.v-notification--red .v-notification__close * {
  stroke: var(--color-red);
}

.v-notification--purple {
  color: var(--color-purple-dark);
  background-color: var(--color-purple-light);
  border-color: var(--color-purple);
}

.v-notification--purple .v-notification__close * {
  stroke: var(--color-purple-dark);
}

.v-notification--dark {
  color: #fff;
  background-color: var(--color-grey-80);
  border-color: var(--color-grey-80);
}

.v-notification--dark .v-notification__close * {
  stroke: #fff;
}

.v-notification__group {
  position: relative;
}

.v-notification__title {
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: 16px;
}

.v-notification.is-closable .v-notification__title {
  margin-right: 20px;
}

.v-notification.has-message .v-notification__title {
  margin-bottom: 6px;
}

.v-notification__content {
  font-size: 14px;
  margin-right: 20px;

  p {
    margin: 0;
  }
}

.v-notification__close {
  position: absolute;
  top: 14px;
  right: 14px;
  padding: 3px;
  cursor: pointer;
  border-radius: 21px;
  transition: opacity var(--transition);

  &:hover {
    opacity: 0.5;
  }
}

.v-notification:not(.has-message) .v-notification__close {
  top: 18px;
}

.v-progress--notification {
  position: absolute;
  bottom: 0px;
  left: 0;
}
</style>
